import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  CartesianGrid,
} from "recharts";

// Define types for our props and data
export interface BarChartDataItem {
  label: string;
  value: number;
}

export interface BarChartProps {
  data: BarChartDataItem[];
  height?: string | number;
  barSize?: number;
  highlightColor?: string;
  regularColor?: string;
  labelRotation?: number;
  tickCount?: number;
  showTooltip?: boolean;
  showGrid?: boolean;
  margins?: { top?: number; right?: number; bottom?: number; left?: number };
  onBarClick?: (item: BarChartDataItem, index: number) => void;
  xAxisTitle?: string;
  yAxisTitle?: string;
}

const CustomBarChart: React.FC<BarChartProps> = ({
  data,
  height = "100%",
  barSize,
  regularColor = "#1AB8C8",
  labelRotation = -45,
  tickCount = 6,
  showTooltip = true,
  showGrid = true,
  margins = { top: 30, right: 30, left: 0, bottom: 40 },
  onBarClick,
  xAxisTitle,
  yAxisTitle,
}) => {
  const [selectedBarIndex, setSelectedBarIndex] = useState<number | null>(null);

  const chartData = data?.map((item, index, array) => ({
    label: item.label,
    value: item.value,
  }));

  const calculatedBarSize = barSize || (window.innerWidth < 400 ? 30 : 55);

  const handleBarClick = (data: any, index: number, e: React.MouseEvent) => {
    // Stop event propagation to prevent the chart click handler from being called
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    
    setSelectedBarIndex(selectedBarIndex === index ? null : index);
    if (onBarClick) {
      onBarClick(data, index);
    }
  };

  const handleChartClick = () => {
    // Reset the selected bar when clicking anywhere inside chart area
    // but outside of bars
    setSelectedBarIndex(null);
  };

  return (
    <>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={chartData}
          margin={{
            top: margins.top || 20,
            right: margins.right || 30,
            left: margins.left || 0,
            bottom: margins.bottom || 40,
          }}
          onClick={handleChartClick}
        >
          <defs>
            <pattern
              id="stripe-pattern"
              patternUnits="userSpaceOnUse"
              width="8"
              height="8"
              patternTransform="rotate(45)"
            >
              <rect width="8" height="8" fill={regularColor} />
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="8"
                stroke="#000513"
                strokeWidth="14"
              />
            </pattern>
          </defs>
          {showGrid && (
            <CartesianGrid
              horizontal={true}
              vertical={false}
              strokeDasharray="3 3"
              stroke="rgba(255, 255, 255, 0.5)"
              strokeOpacity={0.6}
            />
          )}
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={(props) => {
              const { x, y, payload } = props;
              return (
                <text
                  x={x}
                  y={y}
                  dy={10}
                  dx={-10}
                  fill="rgba(255, 255, 255, 0.8)"
                  fontSize={ xAxisTitle ? 14 : 12}
                  textAnchor="end"
                  transform={`rotate(${labelRotation}, ${x}, ${y})`}
                >
                  {payload.value}
                </text>
              );
            }}
            height={xAxisTitle ? 100 : 60}
            interval={0}
            label={xAxisTitle ? {
              value: xAxisTitle,
              position: "insideBottom",
              offset: -40,
              fill: "rgba(255, 255, 255)",
              fontSize: 16,
              fontWeight: 500,
              textAnchor: "middle",
            } : undefined}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: "rgba(255, 255, 255, 0.8)", fontSize: yAxisTitle ? 14 : 12 }}
            tickCount={tickCount}
            strokeDasharray="1 3"
            stroke="#33444F"
            {...(yAxisTitle && { width: 100 })}
            label={yAxisTitle ? {
              value: yAxisTitle,
              angle: -90,
              position: "insideLeft",
              style: { textAnchor: 'middle' },
              fill: "rgba(255, 255, 255)",
              fontSize: 16,
              fontWeight: 500,
              offset: 30,
            } : undefined}
          />
          {showTooltip && (
            <Tooltip
              cursor={false}
              contentStyle={{
                backgroundColor: "#363441",
                borderColor: "#363441",
                outline: "none !important",
                color: "white",
                padding: "4px 8px",
                fontSize: "10px",
                minWidth: "auto",
                borderRadius: "5px",

              }}
              labelStyle={{ display: 'none' }}
              itemStyle={{ color: "white", fontSize: "10px", padding: "2px 0" ,fontWeight:600}}
              formatter={(value) => [
                `${value}`,
              ]}
            />
          )}
          <Bar
            dataKey="value"
            radius={[5, 5, 0, 0]}
            barSize={calculatedBarSize}
            onClick={handleBarClick}
          >
            {chartData?.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={regularColor}
                fillOpacity={
                  selectedBarIndex === null || selectedBarIndex === index
                    ? 1
                    : 0.3
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

export default CustomBarChart;
